import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Boxes, AlertTriangle, AlertCircle, CheckCircle2, Search, Plus, Minus,
  Edit3, History, Package, ToggleLeft, ToggleRight, Loader2, X,
  Upload, Download, FileSpreadsheet
} from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

const STATUS_BADGES = {
  ok: { label: 'OK', class: 'bg-green-500/20 text-green-300 border-green-500/30' },
  low: { label: 'Estoque baixo', class: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
  out: { label: 'Esgotado', class: 'bg-red-500/20 text-red-300 border-red-500/30' },
  untracked: { label: 'Sem rastreio', class: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
};

const ACTION_LABELS = {
  default: 'Usar regra da loja',
  hide: 'Ocultar do cardápio',
  show_unavailable: 'Mostrar como esgotado',
  allow_backorder: 'Permitir mesmo zerado',
};

export default function StockManagement({ token }) {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [additionals, setAdditionals] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('products'); // products | additionals | movements | settings
  const [movementModal, setMovementModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, ad, m, a] = await Promise.all([
        axios.get(`${API}/stock/settings`, { headers }),
        axios.get(`${API}/stock/products`, { headers }),
        axios.get(`${API}/stock/additionals`, { headers }),
        axios.get(`${API}/stock/movements?limit=100`, { headers }),
        axios.get(`${API}/stock/alerts`, { headers }),
      ]);
      setSettings(s.data);
      setProducts(p.data);
      setAdditionals(ad.data);
      setMovements(m.data);
      setAlerts(a.data);
    } catch (e) {
      toast.error('Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const updateSetting = async (key, value) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/stock/settings`, { [key]: value }, { headers });
      setSettings(r.data);
      toast.success('Configuração salva');
      loadAll();
    } catch (e) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const toggleProductTracking = async (product, isAdditional = false) => {
    try {
      const path = isAdditional
        ? `${API}/stock/additionals/${product.additional_id}`
        : `${API}/stock/products/${product.product_id}`;
      await axios.patch(
        path,
        { track_stock: !product.track_stock },
        { headers }
      );
      toast.success(product.track_stock ? 'Rastreio desativado' : 'Rastreio ativado');
      loadAll();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const filtered = products.filter((p) => {
    if (search && !p.product_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const enabled = settings?.stock_control_enabled;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Boxes className="w-7 h-7 text-orange-400" />
            Controle de Estoque
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {enabled
              ? 'Estoque ativo: pedidos descontam automaticamente do saldo'
              : 'Ative o controle para começar a rastrear seu estoque'}
          </p>
        </div>
        {alerts?.enabled && (
          <div className="flex gap-3">
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-300 font-medium">{alerts.out_of_stock_count}</span>
              <span className="text-xs text-red-400/70">esgotados</span>
            </div>
            <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-300 font-medium">{alerts.low_stock_count}</span>
              <span className="text-xs text-yellow-400/70">baixos</span>
            </div>
          </div>
        )}
      </div>

      {/* Toggle global */}
      <div className="glass-card p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
              {enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="font-semibold text-white">
                Controle de estoque {enabled ? 'ativo' : 'desativado'}
              </h3>
              <p className="text-xs text-gray-400">
                {enabled
                  ? 'Cada pedido aprovado decrementa o estoque dos produtos rastreados'
                  : 'Quando ativar, configure os produtos que deseja rastrear'}
              </p>
            </div>
          </div>
          <button
            onClick={() => updateSetting('stock_control_enabled', !enabled)}
            disabled={saving}
            data-testid="toggle-stock-control"
            className={`px-5 py-2.5 rounded-lg font-medium transition ${
              enabled
                ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:opacity-90'
            }`}
          >
            {enabled ? 'Desativar' : 'Ativar agora'}
          </button>
        </div>
      </div>

      {!enabled ? (
        <EmptyState onActivate={() => updateSetting('stock_control_enabled', true)} />
      ) : (
        <>
          {/* Sub-tabs */}
          <div className="flex gap-2 border-b border-white/10 items-center justify-between flex-wrap">
            <div className="flex gap-2">
              {[
                { id: 'products', label: 'Produtos', icon: Package },
                { id: 'additionals', label: 'Adicionais', icon: Plus },
                { id: 'movements', label: 'Histórico', icon: History },
                { id: 'settings', label: 'Configurações', icon: Edit3 },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2.5 flex items-center gap-2 text-sm font-medium transition border-b-2 -mb-px ${
                    activeTab === t.id
                      ? 'text-orange-400 border-orange-400'
                      : 'text-gray-400 border-transparent hover:text-gray-200'
                  }`}
                >
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </button>
              ))}
            </div>
            {(activeTab === 'products' || activeTab === 'additionals') && (
              <button
                onClick={() => setCsvModalOpen(true)}
                data-testid="open-csv-import"
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-sm border border-blue-500/30"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Importar CSV
              </button>
            )}
          </div>

          {activeTab === 'products' && (
            <ProductsTable
              products={filtered}
              search={search}
              setSearch={setSearch}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              onToggle={(p) => toggleProductTracking(p)}
              onMovement={(p) => setMovementModal({ ...p, _isAdditional: false })}
              onEdit={(p) => setEditModal({ ...p, _isAdditional: false })}
            />
          )}
          {activeTab === 'additionals' && (
            <AdditionalsTable
              additionals={additionals}
              search={search}
              setSearch={setSearch}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              onToggle={(a) => toggleProductTracking(a, true)}
              onMovement={(a) => setMovementModal({
                product_id: a.additional_id,
                product_name: a.additional_name,
                stock_quantity: a.stock_quantity,
                stock_unit: a.stock_unit,
                _isAdditional: true,
              })}
              onEdit={(a) => setEditModal({
                product_id: a.additional_id,
                product_name: a.additional_name,
                track_stock: a.track_stock,
                stock_quantity: a.stock_quantity,
                low_stock_threshold: a.low_stock_threshold,
                stock_unit: a.stock_unit,
                out_of_stock_action: a.out_of_stock_action,
                _isAdditional: true,
              })}
            />
          )}
          {activeTab === 'movements' && <MovementsTable movements={movements} />}
          {activeTab === 'settings' && (
            <SettingsPanel
              settings={settings}
              onUpdate={updateSetting}
              saving={saving}
            />
          )}
        </>
      )}

      {movementModal && (
        <MovementModal
          product={movementModal}
          isAdditional={!!movementModal._isAdditional}
          token={token}
          onClose={() => setMovementModal(null)}
          onSaved={() => {
            setMovementModal(null);
            loadAll();
          }}
        />
      )}

      {editModal && (
        <EditProductStockModal
          product={editModal}
          isAdditional={!!editModal._isAdditional}
          token={token}
          onClose={() => setEditModal(null)}
          onSaved={() => {
            setEditModal(null);
            loadAll();
          }}
        />
      )}

      {csvModalOpen && (
        <CsvImportModal
          token={token}
          onClose={() => setCsvModalOpen(false)}
          onSaved={() => {
            setCsvModalOpen(false);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// SUB-COMPONENTES
// ============================================================

function EmptyState({ onActivate }) {
  return (
    <div className="glass-card p-10 text-center">
      <Boxes className="w-20 h-20 mx-auto text-gray-600 mb-4" />
      <h3 className="text-xl font-semibold text-white mb-2">
        Você ainda não usa controle de estoque
      </h3>
      <p className="text-gray-400 max-w-md mx-auto mb-6">
        Ative para que cada pedido desconte automaticamente do saldo dos seus produtos.
        Você decide quais produtos rastrear — bebidas e adicionais podem continuar livres.
      </p>
      <button
        onClick={onActivate}
        data-testid="activate-stock-empty"
        className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:opacity-90"
      >
        Ativar controle de estoque
      </button>
    </div>
  );
}

function ProductsTable({ products, search, setSearch, filterStatus, setFilterStatus, onToggle, onMovement, onEdit }) {
  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar produto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="stock-search"
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="stock-filter-status"
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-orange-500/50"
        >
          <option value="all">Todos</option>
          <option value="out">🔴 Esgotados</option>
          <option value="low">🟡 Estoque baixo</option>
          <option value="ok">🟢 OK</option>
          <option value="untracked">⚪ Sem rastreio</option>
        </select>
      </div>

      {/* Lista */}
      {products.length === 0 ? (
        <div className="glass-card p-10 text-center text-gray-400">Nenhum produto encontrado</div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="px-4 py-3">Produto</th>
                  <th className="px-4 py-3">Categoria</th>
                  <th className="px-4 py-3 text-center">Rastreia</th>
                  <th className="px-4 py-3 text-right">Saldo</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {products.map((p) => {
                  const badge = STATUS_BADGES[p.status] || STATUS_BADGES.untracked;
                  return (
                    <tr key={p.product_id} className="hover:bg-white/5" data-testid={`stock-row-${p.product_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {p.image_url && (
                            <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div>
                            <div className="font-medium text-white">{p.product_name}</div>
                            {p.price && (
                              <div className="text-xs text-gray-500">R$ {p.price.toFixed(2)}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{p.category_name || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => onToggle(p)}
                          data-testid={`toggle-track-${p.product_id}`}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                            p.track_stock ? 'bg-orange-500' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                              p.track_stock ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.track_stock ? (
                          <span className="font-mono text-white">
                            {p.stock_quantity}
                            <span className="text-xs text-gray-500 ml-1">{p.stock_unit}</span>
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs border ${badge.class}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {p.track_stock && (
                            <button
                              onClick={() => onMovement(p)}
                              data-testid={`movement-btn-${p.product_id}`}
                              className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs"
                            >
                              Movimentar
                            </button>
                          )}
                          <button
                            onClick={() => onEdit(p)}
                            data-testid={`edit-btn-${p.product_id}`}
                            className="px-2 py-1 bg-white/10 hover:bg-white/20 text-gray-300 rounded text-xs"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MovementsTable({ movements }) {
  if (movements.length === 0) {
    return (
      <div className="glass-card p-10 text-center text-gray-400">
        Nenhuma movimentação ainda
      </div>
    );
  }

  const TYPE_BADGES = {
    venda: { label: 'Venda', class: 'bg-blue-500/20 text-blue-300' },
    cancelamento: { label: 'Cancelamento', class: 'bg-purple-500/20 text-purple-300' },
    entrada: { label: 'Entrada', class: 'bg-green-500/20 text-green-300' },
    saida: { label: 'Saída', class: 'bg-orange-500/20 text-orange-300' },
    ajuste: { label: 'Ajuste', class: 'bg-yellow-500/20 text-yellow-300' },
    perda: { label: 'Perda', class: 'bg-red-500/20 text-red-300' },
    inicial: { label: 'Inicial', class: 'bg-gray-500/20 text-gray-300' },
  };

  return (
    <div className="glass-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-xs uppercase text-gray-400">
              <th className="px-4 py-3">Data/Hora</th>
              <th className="px-4 py-3">Produto</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3 text-right">Qtd</th>
              <th className="px-4 py-3 text-right">Saldo</th>
              <th className="px-4 py-3">Motivo / Pedido</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {movements.map((m) => {
              const badge = TYPE_BADGES[m.type] || { label: m.type, class: 'bg-gray-500/20 text-gray-300' };
              const date = new Date(m.created_at);
              return (
                <tr key={m.id} className="hover:bg-white/5">
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </td>
                  <td className="px-4 py-3 text-sm text-white">{m.product_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${badge.class}`}>{badge.label}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    <span className={m.quantity >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {m.quantity >= 0 ? '+' : ''}
                      {m.quantity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-gray-400 font-mono">
                    {m.quantity_before} → <span className="text-white">{m.quantity_after}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {m.order_number ? `Pedido #${m.order_number}` : m.reason || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsPanel({ settings, onUpdate, saving }) {
  const items = [
    {
      key: 'stock_auto_disable_at_zero',
      label: 'Ocultar produto do cardápio quando zerar',
      desc: 'Por padrão, produtos esgotados somem do cardápio público. Cada produto pode ter regra própria.',
    },
    {
      key: 'stock_show_quantity_to_customer',
      label: 'Mostrar quantidade ao cliente',
      desc: 'Exibe "Restam apenas X!" no cardápio público (gera urgência).',
    },
    {
      key: 'stock_track_additionals',
      label: 'Rastrear estoque de adicionais',
      desc: 'Permite controlar saldo de itens como queijo extra, bacon, etc.',
    },
  ];

  return (
    <div className="glass-card p-6 space-y-5">
      <div className="space-y-1">
        <label className="text-sm text-gray-300 font-medium">Alerta de estoque baixo (padrão)</label>
        <p className="text-xs text-gray-500 mb-2">
          Produtos abaixo deste saldo ficam marcados como &quot;estoque baixo&quot; no painel
        </p>
        <input
          type="number"
          min="0"
          max="9999"
          value={settings?.stock_low_threshold_default || 5}
          onChange={(e) => onUpdate('stock_low_threshold_default', parseInt(e.target.value) || 0)}
          disabled={saving}
          data-testid="setting-low-threshold"
          className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
        />
      </div>

      <div className="border-t border-white/10 pt-5 space-y-4">
        {items.map((item) => (
          <label
            key={item.key}
            className="flex items-start justify-between gap-4 cursor-pointer"
            data-testid={`setting-${item.key}`}
          >
            <div className="flex-1">
              <div className="text-sm font-medium text-white">{item.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
            </div>
            <button
              type="button"
              onClick={() => onUpdate(item.key, !settings?.[item.key])}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                settings?.[item.key] ? 'bg-orange-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  settings?.[item.key] ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        ))}
      </div>
    </div>
  );
}

function MovementModal({ product, token, isAdditional = false, onClose, onSaved }) {
  const [type, setType] = useState('entrada');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      toast.error('Informe uma quantidade válida');
      return;
    }
    setSubmitting(true);
    try {
      const path = isAdditional
        ? `${API}/stock/additionals/${product.product_id}/movement`
        : `${API}/stock/products/${product.product_id}/movement`;
      await axios.post(
        path,
        { type, quantity: qty, reason: reason || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Movimentação registrada');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const TYPES = [
    { v: 'entrada', label: '➕ Entrada (recebimento)' },
    { v: 'saida', label: '➖ Saída (uso interno)' },
    { v: 'ajuste', label: '🔧 Ajuste (definir saldo absoluto)' },
    { v: 'perda', label: '🗑️ Perda / Quebra' },
    { v: 'inicial', label: '🆕 Carga inicial' },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-modal w-full max-w-md p-6 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Movimentar estoque</h3>
            <p className="text-sm text-gray-400">{product.product_name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Saldo atual: <span className="text-white font-mono">{product.stock_quantity} {product.stock_unit}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-gray-400 uppercase mb-1 block">Tipo</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="movement-type"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            >
              {TYPES.map((t) => (
                <option key={t.v} value={t.v}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase mb-1 block">
              {type === 'ajuste' ? 'Novo saldo' : 'Quantidade'}
            </label>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="movement-quantity"
              autoFocus
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
            />
            {type === 'ajuste' && (
              <p className="text-xs text-yellow-500/70 mt-1">
                ⚠️ Ajuste define o saldo total (não soma).
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase mb-1 block">Motivo (opcional)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Recebimento NF 1234, Quebra, etc."
              data-testid="movement-reason"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting || !quantity}
            data-testid="movement-submit"
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {submitting ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProductStockModal({ product, token, isAdditional = false, onClose, onSaved }) {
  const [trackStock, setTrackStock] = useState(product.track_stock);
  const [quantity, setQuantity] = useState(product.stock_quantity || 0);
  const [threshold, setThreshold] = useState(product.low_stock_threshold || '');
  const [unit, setUnit] = useState(product.stock_unit || 'un');
  const [action, setAction] = useState(product.out_of_stock_action || 'default');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        track_stock: trackStock,
        stock_quantity: parseFloat(quantity) || 0,
        low_stock_threshold: threshold !== '' ? parseInt(threshold) : null,
        stock_unit: unit,
        out_of_stock_action: action,
      };
      const path = isAdditional
        ? `${API}/stock/additionals/${product.product_id}`
        : `${API}/stock/products/${product.product_id}`;
      await axios.patch(
        path,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuração salva');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-modal w-full max-w-md p-6 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Configurar estoque</h3>
            <p className="text-sm text-gray-400">{product.product_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-sm font-medium text-white">Rastrear estoque deste produto</div>
              <div className="text-xs text-gray-500">Ative para descontar a cada pedido</div>
            </div>
            <button
              type="button"
              onClick={() => setTrackStock(!trackStock)}
              data-testid="edit-track-toggle"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                trackStock ? 'bg-orange-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  trackStock ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>

          {trackStock && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 uppercase mb-1 block">Saldo atual</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    data-testid="edit-quantity"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 uppercase mb-1 block">Unidade</label>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    data-testid="edit-unit"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                  >
                    {['un', 'kg', 'g', 'L', 'ml', 'dz', 'pct'].map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">
                  Alertar quando saldo for menor que (opcional)
                </label>
                <input
                  type="number"
                  min="0"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  placeholder="Usar padrão da loja"
                  data-testid="edit-threshold"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase mb-1 block">
                  Quando zerar
                </label>
                <select
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  data-testid="edit-action"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                >
                  {Object.entries(ACTION_LABELS).map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="edit-submit"
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {submitting ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}



// ============================================================
// ADDITIONALS TABLE (espelho da ProductsTable)
// ============================================================

function AdditionalsTable({ additionals, search, setSearch, filterStatus, setFilterStatus, onToggle, onMovement, onEdit }) {
  const filtered = additionals.filter((a) => {
    if (search && !a.additional_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar adicional…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="stock-search-additionals"
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="stock-filter-status-additionals"
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-orange-500/50"
        >
          <option value="all">Todos</option>
          <option value="out">🔴 Esgotados</option>
          <option value="low">🟡 Estoque baixo</option>
          <option value="ok">🟢 OK</option>
          <option value="untracked">⚪ Sem rastreio</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card p-10 text-center text-gray-400">
          {additionals.length === 0
            ? 'Você ainda não cadastrou adicionais. Cadastre na aba "Adicionais" do menu.'
            : 'Nenhum adicional encontrado com os filtros atuais'}
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr className="text-left text-xs uppercase text-gray-400">
                  <th className="px-4 py-3">Adicional</th>
                  <th className="px-4 py-3">Grupo(s)</th>
                  <th className="px-4 py-3 text-center">Rastreia</th>
                  <th className="px-4 py-3 text-right">Saldo</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((a) => {
                  const badge = STATUS_BADGES[a.status] || STATUS_BADGES.untracked;
                  return (
                    <tr key={a.additional_id} className="hover:bg-white/5" data-testid={`stock-row-additional-${a.additional_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{a.additional_name}</div>
                        {a.price !== null && a.price !== undefined && (
                          <div className="text-xs text-gray-500">+ R$ {Number(a.price).toFixed(2)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {(a.group_names && a.group_names.length > 0) ? a.group_names.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => onToggle(a)}
                          data-testid={`toggle-track-additional-${a.additional_id}`}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                            a.track_stock ? 'bg-orange-500' : 'bg-gray-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${a.track_stock ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {a.track_stock ? (
                          <span className="font-mono text-white">
                            {a.stock_quantity}
                            <span className="text-xs text-gray-500 ml-1">{a.stock_unit}</span>
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded text-xs border ${badge.class}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {a.track_stock && (
                            <button
                              onClick={() => onMovement(a)}
                              data-testid={`movement-btn-additional-${a.additional_id}`}
                              className="px-2 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded text-xs"
                            >
                              Movimentar
                            </button>
                          )}
                          <button
                            onClick={() => onEdit(a)}
                            data-testid={`edit-btn-additional-${a.additional_id}`}
                            className="px-2 py-1 bg-white/10 hover:bg-white/20 text-gray-300 rounded text-xs"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CSV IMPORT MODAL
// ============================================================

function CsvImportModal({ token, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/stock/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text',
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_estoque.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erro ao baixar template');
    }
  };

  const doDryRun = async (selectedFile) => {
    if (!selectedFile) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const r = await axios.post(
        `${API}/stock/import-csv?dry_run=true`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao analisar CSV');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setPreview(null);
    setResult(null);
    if (f) doDryRun(f);
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(
        `${API}/stock/import-csv`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(r.data);
      toast.success(`${r.data.updated} produto(s) atualizado(s)`);
      setTimeout(() => onSaved(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao importar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="glass-modal w-full max-w-xl p-6 rounded-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Importar estoque via CSV</h3>
            <p className="text-sm text-gray-400">Atualize saldos em massa pelo nome do produto</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <button
            onClick={downloadTemplate}
            data-testid="csv-download-template"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 rounded-lg"
          >
            <Download className="w-4 h-4" />
            Baixar template CSV
          </button>

          <div>
            <label className="text-xs text-gray-400 uppercase mb-2 block">
              Selecione o arquivo CSV
            </label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              data-testid="csv-file-input"
              className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:bg-orange-500 file:text-white hover:file:bg-orange-600 file:cursor-pointer cursor-pointer"
            />
          </div>

          {preview && !result && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-4 max-h-64 overflow-auto">
              <p className="text-sm text-gray-300 mb-2">
                Encontradas <span className="font-bold text-white">{preview.rows_parsed}</span> linha(s):
              </p>
              <div className="space-y-1 text-xs font-mono text-gray-400">
                {preview.rows.slice(0, 10).map((row, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-white">{row.product_name}</span>
                    <span>
                      qty={row.stock_quantity ?? '—'} · track={String(row.track_stock)}
                    </span>
                  </div>
                ))}
                {preview.rows_parsed > 10 && (
                  <div className="text-gray-500 italic mt-1">+{preview.rows_parsed - 10} mais…</div>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="text-sm text-green-300">
                ✅ <span className="font-bold">{result.updated}</span> de {result.total_rows} produto(s) atualizado(s)
              </div>
              {result.not_found.length > 0 && (
                <div className="text-xs text-yellow-400 mt-2">
                  ⚠️ Não encontrados: {result.not_found.slice(0, 5).join(', ')}
                  {result.not_found.length > 5 && ` (+${result.not_found.length - 5})`}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg"
          >
            Fechar
          </button>
          <button
            onClick={submit}
            disabled={submitting || !file || result}
            data-testid="csv-import-submit"
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {submitting ? 'Processando…' : 'Importar agora'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STANDALONE: BANNER DE ALERTAS (usado no Dashboard de Pedidos)
// ============================================================

export function StockAlertsBanner({ token }) {
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAlerts = async () => {
      try {
        const r = await axios.get(`${API}/stock/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAlerts(r.data);
      } catch (e) {
        // silencioso
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000); // refresh a cada minuto
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  if (!alerts || !alerts.enabled) return null;
  if (alerts.out_of_stock_count === 0 && alerts.low_stock_count === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap gap-3" data-testid="stock-alerts-banner">
      {alerts.out_of_stock_count > 0 && (
        <div className="flex-1 min-w-[260px] p-4 bg-gradient-to-br from-red-500/10 to-red-600/10 border border-red-500/30 rounded-xl flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-red-300 text-lg leading-none">{alerts.out_of_stock_count}</div>
            <div className="text-xs text-red-400/80">produto(s) esgotado(s)</div>
            {alerts.out_of_stock_products.slice(0, 2).map((p) => (
              <div key={p.id} className="text-xs text-gray-400 mt-0.5">• {p.name}</div>
            ))}
          </div>
        </div>
      )}
      {alerts.low_stock_count > 0 && (
        <div className="flex-1 min-w-[260px] p-4 bg-gradient-to-br from-yellow-500/10 to-amber-600/10 border border-yellow-500/30 rounded-xl flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-yellow-300 text-lg leading-none">{alerts.low_stock_count}</div>
            <div className="text-xs text-yellow-400/80">com estoque baixo</div>
            {alerts.low_stock_products.slice(0, 2).map((p) => (
              <div key={p.id} className="text-xs text-gray-400 mt-0.5">
                • {p.name} ({p.quantity} restante)
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
